import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Loader2, PenLine } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { CellEditor } from "@/components/admin/CellEditor";
import { RecordViolations } from "@/components/admin/reference-data/RecordViolations";
import { Pill } from "@/components/brand/Pill";
import { useStageReferenceDataEdits } from "@/hooks/useApi";
import { toInputString } from "@/lib/reference-data-cell";
import {
  buildEditOperations,
  changedCells,
  errMessage,
  fieldKind,
  recordViolations,
} from "@/lib/reference-data-edit";
import type { ReferenceDataColumn } from "@/types/reference-data";

const INPUT_CLASS =
  "w-full rounded-md border border-[#e8ebf0] bg-white px-2.5 py-1.5 font-mono text-[12px] " +
  "text-[#08254c] outline-none focus:border-[#08254c]";

/**
 * Edit ONE record in a form, instead of hunting for its cell in the batch grid.
 *
 * Same governance as the batch flow: saving stages a **draft** version
 * (`current_version = FALSE`) and nothing goes live until it is promoted in
 * *Versions & audit* — which is where `onStaged` sends the user. It reuses the
 * `/records/edit` endpoint through `useStageReferenceDataEdits`, so there is no
 * second write path to keep in sync.
 *
 * Only the columns the user actually touched are sent (`changedCells`); the
 * backend overlays them on the active row, so untouched columns carry into the
 * new version untouched.
 */
export function RecordEditDialog({
  tableName,
  row,
  columns,
  naturalKey,
  onOpenChange,
  onStaged,
}: {
  tableName: string;
  /** The record being edited; `null` keeps the dialog closed. */
  row: Record<string, unknown> | null;
  columns: ReferenceDataColumn[];
  naturalKey: string[];
  onOpenChange: (open: boolean) => void;
  onStaged?: () => void;
}) {
  const { t } = useTranslation();
  const stageMutation = useStageReferenceDataEdits(tableName);
  const [violations, setViolations] = useState<string[]>([]);
  const [draft, setDraft] = useState<Record<string, string>>({});

  const rowId = row?.id === undefined ? null : String(row.id);
  const keySet = useMemo(() => new Set(naturalKey), [naturalKey]);

  // Seed the draft once per opening. Keyed on the record *id* and on closing —
  // never on the row object's identity: react-query refetches on window focus
  // (staleTime 30s) and hands back a fresh object for the same record, so
  // re-seeding on identity would wipe whatever the user had typed. Closing
  // clears the marker, so reopening the same record starts from the row again
  // instead of resurrecting an abandoned draft.
  const seededFor = useRef<string | null>(null);
  if (!row) {
    seededFor.current = null;
  } else if (rowId !== null && seededFor.current !== rowId) {
    seededFor.current = rowId;
    const seeded: Record<string, string> = {};
    for (const column of columns) seeded[column.name] = toInputString(row[column.name]);
    // Adjusting state during render (React's documented "derive state from a
    // prop change" pattern): the marker above makes it run once, not in a loop.
    setDraft(seeded);
    setViolations([]);
  }

  const changed = row ? changedCells(row, draft) : {};
  const dirtyCount = Object.keys(changed).length;

  const handleSubmit = async () => {
    if (!row || rowId === null) return;
    const operations = buildEditOperations({ [rowId]: changed }, []);
    if (!operations.length) {
      toast.error(t("Nothing changed yet — edit a field first."));
      return;
    }
    setViolations([]);
    try {
      const res = await stageMutation.mutateAsync(operations);
      toast.success(
        t("Staged {{n}} change(s) as a draft — promote it in Versions & audit.", {
          n: res.report?.row_count ?? 1,
        }),
      );
      onOpenChange(false);
      onStaged?.();
    } catch (err) {
      const rejected = recordViolations(err);
      if (rejected.length) {
        setViolations(rejected);
      } else {
        toast.error(errMessage(err, t("Could not stage the edits.")));
      }
    }
  };

  return (
    <Dialog open={!!row} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[640px]">
        <DialogHeader>
          <DialogTitle className="font-brand text-[#08254c]">{t("Edit this record")}</DialogTitle>
          <DialogDescription className="text-[#596b85] leading-relaxed">
            {t(
              "Edit a field and save it as a draft. Nothing goes live until you promote it in Versions & audit.",
            )}
          </DialogDescription>
        </DialogHeader>

        {/* A real <form> so Enter submits and Esc (Radix) cancels. */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void handleSubmit();
          }}
        >
          <div className="max-h-[60vh] overflow-y-auto -mx-1 px-1 space-y-3.5 py-1">
            <RecordViolations violations={violations} />
            {columns.map((column) => {
              const fieldId = `record-field-${column.name}`;
              const value = draft[column.name] ?? "";
              const kind = fieldKind(column, value);
              const dirty = column.name in changed;
              const className = `${INPUT_CLASS} ${dirty ? "border-amber-400 bg-[#fffdf5]" : ""}`;
              return (
                <div key={column.name}>
                  <div className="flex items-center gap-2 mb-1">
                    <label
                      htmlFor={fieldId}
                      className="mono text-[12px] font-semibold text-[#08254c]"
                    >
                      {column.name}
                      {!column.is_nullable && (
                        <span className="text-[#c25500]" title={t("required")}>
                          {" *"}
                        </span>
                      )}
                    </label>
                    {keySet.has(column.name) && (
                      <span
                        title={t(
                          "Part of the record's key — changing it creates a different record.",
                        )}
                      >
                        <Pill tone="muted">{t("identity")}</Pill>
                      </span>
                    )}
                    <span className="mono muted text-[10px] ml-auto">{column.data_type}</span>
                  </div>
                  {kind === "textarea" ? (
                    <textarea
                      id={fieldId}
                      aria-label={column.name}
                      className={`${className} ${column.data_type === "text" ? "" : "font-mono"} resize-y`}
                      rows={column.data_type === "text" ? 3 : 6}
                      value={value}
                      onChange={(e) =>
                        setDraft((prev) => ({ ...prev, [column.name]: e.target.value }))
                      }
                    />
                  ) : kind === "number" ? (
                    <input
                      id={fieldId}
                      aria-label={column.name}
                      type="number"
                      step="any"
                      className={className}
                      value={value}
                      onChange={(e) =>
                        setDraft((prev) => ({ ...prev, [column.name]: e.target.value }))
                      }
                    />
                  ) : (
                    // `select` and `text` both come from CellEditor, so the
                    // closed-domain rule has a single implementation.
                    <CellEditor
                      id={fieldId}
                      column={column}
                      label={column.name}
                      className={className}
                      value={value}
                      placeholder=""
                      onChange={(next) =>
                        setDraft((prev) => ({ ...prev, [column.name]: next }))
                      }
                    />
                  )}
                </div>
              );
            })}
          </div>

          <p className="mono muted text-[11px] mt-3">{t("A blank field keeps the current value.")}</p>

          <DialogFooter className="mt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t("Cancel")}
            </Button>
            <Button
              type="submit"
              variant="action"
              className="gap-1.5"
              disabled={dirtyCount === 0 || stageMutation.isPending}
            >
              {stageMutation.isPending ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <PenLine className="w-3.5 h-3.5" />
              )}
              {t("Save edit")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
